import { type AsyncStateUpdate } from '@synqo/frontend-component-library';
import { type ApiOrderPosition } from '@Model';
import { useCallback } from 'react';

export enum PaymentMode {
    Incoming,
    Outgoing,
}

export function usePaymentMode(currentPrice: number, requestUpdateState: AsyncStateUpdate<ApiOrderPosition>) {
    const paymentMode = currentPrice >= 0 ? PaymentMode.Incoming : PaymentMode.Outgoing;

    const reversePaymentMode = useCallback(() => {
        return requestUpdateState((latest, saveData) => {
            const previousPriceAbsolute = Math.abs(latest.price_per_unit);

            // we shouldn't simply invert the current price, because this is a queued operation, so the price and its sign
            // might have changed since the user clicked the button. So we ensure the sign manually.
            const newPrice = paymentMode === PaymentMode.Outgoing ? previousPriceAbsolute : -1 * previousPriceAbsolute;

            return saveData({
                ...latest,
                price_per_unit: newPrice,
            });
        });
    }, [paymentMode, requestUpdateState]);

    const ensurePaymentMode = useCallback(
        (value: number) => {
            if (paymentMode === PaymentMode.Incoming) {
                // if the payment mode was incoming before, then keep the sign (even if it's negative now)
                return value;
            }
            if (value === -1 * currentPrice) {
                // if the value differs by exactly a sign, then keep the value (this forces the switch in sign)
                return value;
            }
            if (value > 0) {
                // if the value is positive, but it was negative before, then make it negative
                return -1 * value;
            }
            // Just return the user input value
            return value;
        },
        [paymentMode, currentPrice],
    );

    return {
        reversePaymentMode,
        ensurePaymentMode,
        paymentMode,
    };
}
